import React, { useEffect, useRef } from "react";

type RotaryDialProps = {
  value: number;
  setValue: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
  size?: number;
  sensitivity?: number;
  arcCut?: number;
  backgroundColor?: string;
  fillColor?: string;
  disabledBackgroundColor?: string;
  disabledFillColor?: string;
  disabled?: boolean;
  onStart?: (value: number) => void;
  onChange?: (value: number) => void;
  onEnd?: (value: number) => void;
};

const sliderStrokeWidth = 1.5;
const audimeePrimary = "#80578a";
const audimeeSecondary = "#e8deea";
const m4g1c = 0.6;

const makeSelectable = (selectable: boolean) => {
  const styleId = "audimee-unselectable-style";

  if (selectable) {
    // Remove the style element if it exists
    const existingStyle = document.getElementById(styleId);
    if (existingStyle !== null) {
      document.head.removeChild(existingStyle);
    }
  } else {
    // Create a style element if it doesn't exist
    let style = document.getElementById(styleId);
    if (style === null) {
      style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        * {
          -webkit-user-select: none; /* Safari */
          -moz-user-select: none;    /* Firefox */
          -ms-user-select: none;     /* Internet Explorer/Edge */
          user-select: none;         /* Non-prefixed version, currently supported by Chrome, Edge, Opera and Firefox */
        }
      `;
      document.head.appendChild(style);
    }
  }
};
export function RotaryDial({
  value,
  setValue,
  min = 0,
  max = 100,
  step = 1,
  sensitivity = 1,
  size = 60,
  arcCut = Math.PI / 6,
  backgroundColor = audimeeSecondary,
  fillColor = audimeePrimary,
  disabledBackgroundColor = "#f2eff4",
  disabledFillColor = "#bcaac4",
  disabled,
  onStart,
  onChange,
  onEnd,
}: RotaryDialProps) {
  const dialRef = useRef<SVGSVGElement>(null);
  const isDragging = useRef(false);
  const startValue = useRef(value);
  const curValue = useRef(value);
  const onStartRef = useRef(onStart);
  const onChangeRef = useRef(onChange);
  const onEndRef = useRef(onEnd);
  useEffect(() => {
    onStartRef.current = onStart;
    onChangeRef.current = onChange;
    onEndRef.current = onEnd;
  });

  const handleMove = (clientY: number) => {
    if (isDragging.current && dialRef.current !== null) {
      const rect = dialRef.current.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      const deltaY = centerY - clientY;

      let newValue =
        startValue.current +
        ((deltaY * sensitivity) / rect.height) * (max - min);
      newValue = Math.max(min, Math.min(max, newValue));
      newValue = Math.round(newValue / step) * step;

      setValue(newValue);
      curValue.current = newValue;
      if (onChangeRef.current !== undefined) {
        onChangeRef.current(newValue);
      }
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    handleMove(e.clientY);
  };

  const handleTouchMove = (e: TouchEvent) => {
    e.preventDefault();
    handleMove(e.touches[0].clientY);
  };

  const handleMouseDown = () => {
    makeSelectable(false);
    isDragging.current = true;
    startValue.current = value;
    curValue.current = value;
    if (onStartRef.current !== undefined) {
      onStartRef.current(value);
    }
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    makeSelectable(false);
    isDragging.current = true;
    startValue.current = value;
    curValue.current = value;
    if (onStart !== undefined) {
      onStart(value);
    }
    window.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    window.addEventListener("touchend", handleTouchEnd);
  };

  const handleMouseUp = () => {
    makeSelectable(true);
    isDragging.current = false;
    if (onEndRef.current !== undefined) {
      onEndRef.current(curValue.current);
    }
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleTouchEnd = () => {
    makeSelectable(true);
    isDragging.current = false;
    if (onEnd !== undefined) {
      onEnd(curValue.current);
    }
    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", handleTouchEnd);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);

  const center = size / 2;
  const radius = size / 2 - 3;
  const startAngle = Math.PI / 2 + arcCut;
  const arcFraction = (value - min) / (max - min);
  const endAngle = startAngle + arcFraction * (Math.PI * 2 - arcCut * 2);
  const finalAngle = startAngle + (Math.PI * 2 - arcCut * 2);
  const xStart = (v: number) => center + Math.cos(startAngle) * v * radius;
  const yStart = (v: number) => center + Math.sin(startAngle) * v * radius;
  const xEnd = (v: number) => center + Math.cos(endAngle) * v * radius;
  const yEnd = (v: number) => center + Math.sin(endAngle) * v * radius;
  const xFinal = (v: number) => center + Math.cos(finalAngle) * v * radius;
  const yFinal = (v: number) => center + Math.sin(finalAngle) * v * radius;

  return (
    <svg
      ref={dialRef}
      onMouseDown={disabled === true ? () => {} : handleMouseDown}
      onTouchStart={disabled === true ? () => {} : handleTouchStart}
      width={size}
      height={size}
      className={disabled === true ? "cursor-default" : "cursor-pointer"}
    >
      <circle
        cx={center}
        cy={center}
        r={radius * 0.2}
        fill={disabled === true ? disabledBackgroundColor : backgroundColor}
      />
      <circle
        cx={center}
        cy={center}
        r={radius * 0.2}
        fill={disabled === true ? disabledFillColor : fillColor}
      />
      {[0.2, m4g1c, 1.0].map((v) => (
        <path
          key={v}
          d={`M ${xStart(v)} ${yStart(v)} A ${radius * v} ${radius * v} 0 ${
            finalAngle - startAngle < Math.PI ? 0 : 1
          } 1 ${xFinal(v)} ${yFinal(v)}`}
          strokeWidth={sliderStrokeWidth}
          fill={"none"}
          stroke={disabled === true ? disabledFillColor : fillColor}
        />
      ))}
      <path
        d={`M ${xStart(m4g1c)} ${yStart(m4g1c)} L ${xStart(1)} ${yStart(
          1
        )} A ${radius} ${radius} 0 ${
          endAngle - startAngle < Math.PI ? 0 : 1
        } 1 ${xEnd(1)} ${yEnd(1)} L ${xEnd(m4g1c)} ${yEnd(m4g1c)} A ${
          radius * m4g1c
        } ${radius * m4g1c} 0 ${
          endAngle - startAngle < Math.PI ? 0 : 1
        } 0 ${xStart(m4g1c)} ${yStart(m4g1c)} Z`}
        fill={fillColor}
      />
      <path
        d={`M ${center} ${center} L ${xEnd(1)} ${yEnd(1)} `}
        stroke={disabled === true ? disabledFillColor : fillColor}
        strokeWidth={sliderStrokeWidth}
      />
    </svg>
  );
}
